import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectActiveLocation } from "../../../app/locationSlice";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import {
  FiSave,
  FiRefreshCw,
  FiMapPin,
  FiPhone,
  FiLink,
  FiType,
  FiStar,
  FiInfo,
  FiEye,
  FiSmartphone,
  FiTablet,
  FiCheck,
  FiFileText,
  FiShoppingBag,
  FiDroplet,
  FiGlobe,
  FiMessageSquare,
  FiCheckCircle,
  FiLayout,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiImage,
  FiExternalLink,
  FiCompass,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiSearch,
  FiNavigation,
  FiCheckSquare,
  FiSquare,
  FiList,
} from "react-icons/fi";

const INTER = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };
const SERIF = { fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif" };

// ── Extract Google Drive File ID from any Google Drive / UserContent link ────
export function extractGoogleDriveId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  // 1. Match googleusercontent.com/d/<id>
  const lh3Match = trimmed.match(/googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/i);
  if (lh3Match && lh3Match[1]) return lh3Match[1];
  // 2. Match drive.google.com/file/d/<id>
  const fileDMatch = trimmed.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i);
  if (fileDMatch && fileDMatch[1]) return fileDMatch[1];
  // 3. Match /d/<id> (general format with alphanumeric id >= 20 chars)
  const dMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]{20,})/i);
  if (dMatch && dMatch[1]) return dMatch[1];
  // 4. Match ?id=<id> or &id=<id> in drive.google.com
  const idMatch = trimmed.match(/drive\.google\.com\/.*[?&]id=([a-zA-Z0-9_-]+)/i);
  if (idMatch && idMatch[1]) return idMatch[1];
  return null;
}

// ── URL Normalization (Google Drive / Dropbox / Direct Images) ───────────────
export function normalizeImageUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  const driveId = extractGoogleDriveId(trimmed);
  if (driveId) {
    // Preserve custom sizing if already explicitly specified (e.g. =s400, =w600, =s0)
    const sizeParamMatch = trimmed.match(/(=[swh]\d+|=[swh]\d+-[swh]\d+|=s0)$/i);
    const sizeParam = sizeParamMatch ? sizeParamMatch[0] : "=s800";
    return `https://lh3.googleusercontent.com/d/${driveId}${sizeParam}`;
  }
  if (trimmed.includes("dropbox.com")) {
    return trimmed.replace("dl=0", "raw=1");
  }
  return trimmed;
}

// ── Fallback candidate URLs for maximum resilience against CDN throttling ─────
export function getImageCandidates(url) {
  if (!url || typeof url !== "string") return [];
  const trimmed = url.trim();
  const driveId = extractGoogleDriveId(trimmed);
  if (driveId) {
    const sizeParamMatch = trimmed.match(/(=[swh]\d+|=[swh]\d+-[swh]\d+|=s0)$/i);
    const sizeParam = sizeParamMatch ? sizeParamMatch[0] : "=s800";
    return [
      `https://lh3.googleusercontent.com/d/${driveId}${sizeParam}`,
      `https://lh3.googleusercontent.com/d/${driveId}`,
      `https://drive.google.com/thumbnail?id=${driveId}&sz=w800`,
      `https://drive.google.com/uc?export=view&id=${driveId}`,
    ];
  }
  if (trimmed.includes("dropbox.com")) {
    const raw = trimmed.replace("dl=0", "raw=1");
    return [raw, trimmed];
  }
  return [trimmed];
}

// ── Store Initials Monogram Helper ──────────────────────────────────────────
export function getStoreInitials(name) {
  if (!name || typeof name !== "string") return "S";
  const trimmed = name.trim();
  if (!trimmed) return "S";

  const words = trimmed.split(/[\s,&._-]+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  const single = words[0];
  const upperChars = single.match(/[A-Z]/g);
  if (upperChars && upperChars.length >= 2) {
    return (upperChars[0] + upperChars[1]).toUpperCase();
  }
  return single.slice(0, 2).toUpperCase();
}

// ── Design Token Extraction Helpers for Mobile & API ────────────────────────
export function extractHexColor(str, fallback = "#b78a39") {
  if (!str || typeof str !== "string") return fallback;
  const m = str.match(/#([0-9a-fA-F]{3,8})/);
  return m ? m[0] : fallback;
}

export function extractGradientStops(str, fallback = ["#fdf8eb", "#ebd7ab"]) {
  if (!str || typeof str !== "string") return fallback;
  const m = str.match(/#[0-9a-fA-F]{6}/g);
  return m && m.length >= 2 ? m : fallback;
}

// ── Card Design Templates ───────────────────────────────────────────────────
// [IMPORTANT]: royal_gold is the exact original design matching the approved screenshot.
// All ornament & jewellery variations are created as separate new templates.
const TEMPLATES = [
  {
    id: "royal_gold",
    name: "Royal Heritage",
    subtitle: "Classic Gold Silk (Original)",
    paletteLabel: "Original Gold & Silk",
    previewBg: "linear-gradient(135deg, #fdf8eb, #e8cb7e)",
    swatchMedallion: "radial-gradient(circle, #d8aa49, #8c5d12)",
    swatchInitialColor: "#ffffff",
    cardBg: "linear-gradient(145deg, #fdf8eb 0%, #fefcf7 35%, #f7edd4 70%, #ebd7ab 100%)",
    cardBorder: "border-[#dec58c]/80",
    shadow: "shadow-[0_12px_32px_rgba(164,124,42,0.18)]",
    ringColor: "border-[#cca655]/20",
    gradId: "flourishGold",
    flourishGradStart: "#c5a458",
    flourishGradMid: "#9e7b2c",
    flourishGradEnd: "#7a5717",
    ribbonDark: ["#c5983c", "#9a6e1f", "#caa046", "#7a500f"],
    ribbonMid: ["#e8c775", "#fef3cc", "#d4a342", "#b48325"],
    ribbonFront: ["#d8ab49", "#fef0bd", "#dfaf4d", "#9f721a"],
    ribbonStroke: "#fff5d0",
    medallionOuterBorder: "border-[#d5b062]",
    medallionBg: "radial-gradient(circle at 35% 30%, #d8aa49 0%, #ba892c 55%, #8c5d12 100%)",
    medallionInitialColor: "#fdfaf2",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.45),0_4px_12px_rgba(130,85,15,0.22)]",
    titleColor: "text-[#3d2403]",
    titleDropShadow: "drop-shadow-[0_1px_1px_rgba(255,255,255,0.7)]",
    taglineColor: "text-[#6c4e1b]",
    dividerGrad: "from-transparent via-[#bfa05e] to-[#8d6928]",
    boxBg: "bg-white/92 border-white/95 shadow-[0_4px_16px_rgba(145,107,32,0.08)]",
    boxTitleDefault: "#8f5b13",
    boxSubtextColor: "text-[#475569]",
    ornamentType: "none", // 100% untouched original
  },
  {
    id: "temple_jewellery",
    name: "Temple Jewellery",
    subtitle: "22k Gold & Beaded Haar",
    paletteLabel: "22k Antique Gold",
    previewBg: "linear-gradient(135deg, #fdf5e2, #dfb454)",
    swatchMedallion: "radial-gradient(circle, #dfb454, #875f12)",
    swatchInitialColor: "#ffffff",
    cardBg: "linear-gradient(145deg, #fcf4de 0%, #fffbf2 35%, #f6e8c8 70%, #ebd39b 100%)",
    cardBorder: "border-[#dec58c]/90",
    shadow: "shadow-[0_12px_32px_rgba(164,124,42,0.2)]",
    ringColor: "border-[#cca655]/25",
    gradId: "flourishTempleGold",
    flourishGradStart: "#cba554",
    flourishGradMid: "#a17b2b",
    flourishGradEnd: "#785312",
    ribbonDark: ["#c5983c", "#9a6e1f", "#caa046", "#7a500f"],
    ribbonMid: ["#e8c775", "#fef3cc", "#d4a342", "#b48325"],
    ribbonFront: ["#d8ab49", "#fef0bd", "#dfaf4d", "#9f721a"],
    ribbonStroke: "#fff5d0",
    medallionOuterBorder: "border-[#d5b062]",
    medallionBg: "radial-gradient(circle at 35% 30%, #d8aa49 0%, #ba892c 55%, #8c5d12 100%)",
    medallionInitialColor: "#fdfaf2",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.45),0_4px_12px_rgba(130,85,15,0.22)]",
    titleColor: "text-[#3d2403]",
    titleDropShadow: "drop-shadow-[0_1px_1px_rgba(255,255,255,0.7)]",
    taglineColor: "text-[#6c4e1b]",
    dividerGrad: "from-transparent via-[#bfa05e] to-[#8d6928]",
    boxBg: "bg-white/92 border-white/95 shadow-[0_4px_16px_rgba(145,107,32,0.08)]",
    boxTitleDefault: "#8f5b13",
    boxSubtextColor: "text-[#475569]",
    ornamentType: "garland",
    garlandBeadColor: "#dfb454",
    garlandChainColor: "#b38830",
  },
  {
    id: "diamond_solitaire",
    name: "Diamond Solitaire",
    subtitle: "Platinum & Solitaire Cut",
    paletteLabel: "Platinum & Diamond",
    previewBg: "linear-gradient(135deg, #f0f4f9, #d0dce8)",
    swatchMedallion: "radial-gradient(circle, #ffffff, #8899ac)",
    swatchInitialColor: "#1e293b",
    cardBg: "linear-gradient(145deg, #f8fafc 0%, #ffffff 35%, #e9eff6 70%, #dbe4ee 100%)",
    cardBorder: "border-[#b0c2d4]/80",
    shadow: "shadow-[0_12px_32px_rgba(100,125,155,0.18)]",
    ringColor: "border-[#94a3b8]/25",
    gradId: "flourishPlatinum",
    flourishGradStart: "#94a3b8",
    flourishGradMid: "#64748b",
    flourishGradEnd: "#475569",
    ribbonDark: ["#64748b", "#475569", "#6b7d94", "#334155"],
    ribbonMid: ["#cbd5e1", "#f8fafc", "#94a3b8", "#64748b"],
    ribbonFront: ["#e2e8f0", "#ffffff", "#cbd5e1", "#94a3b8"],
    ribbonStroke: "#ffffff",
    medallionOuterBorder: "border-[#94a3b8]",
    medallionBg: "radial-gradient(circle at 35% 30%, #ffffff 0%, #cbd5e1 55%, #64748b 100%)",
    medallionInitialColor: "#0f172a",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_14px_rgba(100,116,139,0.3)]",
    titleColor: "text-[#0f172a]",
    titleDropShadow: "drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]",
    taglineColor: "text-[#334155]",
    dividerGrad: "from-transparent via-[#94a3b8] to-[#475569]",
    boxBg: "bg-white/94 border-slate-200/80 shadow-[0_4px_16px_rgba(100,116,139,0.1)]",
    boxTitleDefault: "#0f172a",
    boxSubtextColor: "text-[#475569]",
    ornamentType: "diamond",
    sparkleColor: "#38bdf8",
  },
  {
    id: "maharani_ruby",
    name: "Maharani Ruby",
    subtitle: "Bridal Kundan & Jadau",
    paletteLabel: "Ruby Velvet & 24k",
    previewBg: "linear-gradient(135deg, #3d0710, #61101e)",
    swatchMedallion: "radial-gradient(circle, #ffd700, #801223)",
    swatchInitialColor: "#ffd700",
    cardBg: "linear-gradient(145deg, #38060f 0%, #540f1a 35%, #3c0710 70%, #1f0308 100%)",
    cardBorder: "border-[#cca655]/60",
    shadow: "shadow-[0_12px_32px_rgba(56,6,15,0.45)]",
    ringColor: "border-[#e0bc68]/20",
    gradId: "flourishRubyGold",
    flourishGradStart: "#ffea95",
    flourishGradMid: "#d6a946",
    flourishGradEnd: "#875f12",
    ribbonDark: ["#500b16", "#2c040b", "#4a0913", "#1b0206"],
    ribbonMid: ["#f5ce6b", "#fff0b8", "#dca93b", "#8b5f12"],
    ribbonFront: ["#fad982", "#fff5c7", "#e7b952", "#9c6b16"],
    ribbonStroke: "#fff7d6",
    medallionOuterBorder: "border-[#f0ce72]",
    medallionBg: "radial-gradient(circle at 35% 30%, #ffd700 0%, #c49622 40%, #680d1b 100%)",
    medallionInitialColor: "#ffeed1",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.5),0_4px_14px_rgba(240,206,114,0.35)]",
    titleColor: "text-[#ffeed1]",
    titleDropShadow: "drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]",
    taglineColor: "text-[#f0ce86]",
    dividerGrad: "from-transparent via-[#f0ce72] to-[#aa7c22]",
    boxBg: "bg-[#28050b]/88 border-[#cca655]/40 shadow-[0_6px_20px_rgba(0,0,0,0.35)]",
    boxTitleDefault: "#f8d578",
    boxSubtextColor: "text-[#fed7aa]",
    ornamentType: "garland",
    garlandBeadColor: "#ffd700",
    garlandChainColor: "#cca655",
  },
  {
    id: "emerald_royale",
    name: "Emerald Nizam",
    subtitle: "Heritage Polki & Panna",
    paletteLabel: "Royal Emerald & Gold",
    previewBg: "linear-gradient(135deg, #052219, #0c3d2e)",
    swatchMedallion: "radial-gradient(circle, #f9d877, #aa791b)",
    swatchInitialColor: "#052219",
    cardBg: "linear-gradient(145deg, #052219 0%, #0a3628 35%, #06261c 70%, #03150f 100%)",
    cardBorder: "border-[#d4af37]/60",
    shadow: "shadow-[0_12px_32px_rgba(5,34,25,0.45)]",
    ringColor: "border-[#cca655]/20",
    gradId: "flourishEmeraldGold",
    flourishGradStart: "#ffea95",
    flourishGradMid: "#d6a946",
    flourishGradEnd: "#875f12",
    ribbonDark: ["#4a370e", "#2c2005", "#47340b", "#1f1602"],
    ribbonMid: ["#f3cc69", "#fff0b6", "#d9a538", "#885c10"],
    ribbonFront: ["#f9d77e", "#fff4c5", "#e5b64f", "#976715"],
    ribbonStroke: "#fff7d6",
    medallionOuterBorder: "border-[#f0ce72]",
    medallionBg: "radial-gradient(circle at 35% 30%, #fbe08e 0%, #d8aa49 55%, #8f5d13 100%)",
    medallionInitialColor: "#052219",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.6),0_4px_14px_rgba(240,206,114,0.35)]",
    titleColor: "text-[#fdedc0]",
    titleDropShadow: "drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]",
    taglineColor: "text-[#dfc37f]",
    dividerGrad: "from-transparent via-[#f0ce72] to-[#a87a20]",
    boxBg: "bg-[#072d21]/88 border-[#cca655]/40 shadow-[0_6px_20px_rgba(0,0,0,0.3)]",
    boxTitleDefault: "#f8d578",
    boxSubtextColor: "text-[#d1e3dd]",
    ornamentType: "garland",
    garlandBeadColor: "#f6d475",
    garlandChainColor: "#bfa04d",
  },
  {
    id: "rose_gold",
    name: "Rose & Pearl",
    subtitle: "South Sea Pearl & Rose",
    paletteLabel: "Blush & Rose Gold",
    previewBg: "linear-gradient(135deg, #fff5f2, #e8b0a2)",
    swatchMedallion: "radial-gradient(circle, #e8a898, #945041)",
    swatchInitialColor: "#ffffff",
    cardBg: "linear-gradient(145deg, #fff5f2 0%, #fef0eb 35%, #fae0d8 70%, #ebd0c5 100%)",
    cardBorder: "border-[#e5b3a5]/80",
    shadow: "shadow-[0_12px_32px_rgba(180,95,75,0.16)]",
    ringColor: "border-[#d88c7c]/20",
    gradId: "flourishRoseGold",
    flourishGradStart: "#e59888",
    flourishGradMid: "#b86958",
    flourishGradEnd: "#823b2c",
    ribbonDark: ["#bd7262", "#914839", "#be7364", "#6d2f23"],
    ribbonMid: ["#f2b4a7", "#ffe5df", "#d88c7c", "#9c4f40"],
    ribbonFront: ["#e99f91", "#ffd8d0", "#d78473", "#8f4435"],
    ribbonStroke: "#fff0ec",
    medallionOuterBorder: "border-[#df9b8c]",
    medallionBg: "radial-gradient(circle at 35% 30%, #f0afa0 0%, #ca7b6a 55%, #8e4434 100%)",
    medallionInitialColor: "#fff8f6",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.5),0_4px_12px_rgba(142,68,52,0.25)]",
    titleColor: "text-[#451e18]",
    titleDropShadow: "drop-shadow-[0_1px_1px_rgba(255,255,255,0.7)]",
    taglineColor: "text-[#753b31]",
    dividerGrad: "from-transparent via-[#d88c7c] to-[#99493a]",
    boxBg: "bg-white/92 border-white/95 shadow-[0_4px_16px_rgba(180,95,75,0.08)]",
    boxTitleDefault: "#a84e3d",
    boxSubtextColor: "text-[#5e4440]",
    ornamentType: "garland",
    garlandBeadColor: "#fff4f0",
    garlandChainColor: "#d88c7c",
  },
  {
    id: "imperial_navy",
    name: "Midnight Sapphire",
    subtitle: "Showroom 24k Bullion",
    paletteLabel: "Navy & 24k Bullion",
    previewBg: "linear-gradient(135deg, #05142b, #0d2752)",
    swatchMedallion: "radial-gradient(circle, #f9d877, #aa791b)",
    swatchInitialColor: "#05142b",
    cardBg: "linear-gradient(145deg, #05142b 0%, #0c2650 35%, #071936 70%, #030d1c 100%)",
    cardBorder: "border-[#cca655]/60",
    shadow: "shadow-[0_12px_32px_rgba(5,20,43,0.45)]",
    ringColor: "border-[#e0bc68]/20",
    gradId: "flourishNavyGold",
    flourishGradStart: "#ffeb99",
    flourishGradMid: "#d8ab49",
    flourishGradEnd: "#8c6214",
    ribbonDark: ["#63440e", "#3a2505", "#5c3d0b", "#2c1a02"],
    ribbonMid: ["#f5ce6b", "#fff0b8", "#dca93b", "#8b5f12"],
    ribbonFront: ["#fad982", "#fff5c7", "#e7b952", "#9c6b16"],
    ribbonStroke: "#fff7d6",
    medallionOuterBorder: "border-[#f0ce72]",
    medallionBg: "radial-gradient(circle at 35% 30%, #fbe08e 0%, #d8aa49 55%, #8f5d13 100%)",
    medallionInitialColor: "#06152d",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.6),0_4px_14px_rgba(240,206,114,0.35)]",
    titleColor: "text-[#fdedc2]",
    titleDropShadow: "drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]",
    taglineColor: "text-[#ecd38e]",
    dividerGrad: "from-transparent via-[#f0ce72] to-[#aa7c22]",
    boxBg: "bg-[#0a1e3d]/88 border-[#cca655]/40 shadow-[0_6px_20px_rgba(0,0,0,0.3)]",
    boxTitleDefault: "#f8d578",
    boxSubtextColor: "text-slate-300",
    ornamentType: "diamond",
    sparkleColor: "#fde047",
  },
  {
    id: "oceanic_teal",
    name: "Oceanic Teal",
    subtitle: "Petrol Teal & Sand Gold",
    paletteLabel: "Petrol Teal & Sand Gold",
    previewBg: "linear-gradient(135deg, #0e3740, #1d6271)",
    swatchMedallion: "radial-gradient(circle, #fce2b8, #d7ad7b)",
    swatchInitialColor: "#0e3740",
    cardBg: "linear-gradient(145deg, #0e3740 0%, #15505e 35%, #1d6271 70%, #0d343e 100%)",
    cardBorder: "border-[#d7ad7b]/60",
    shadow: "shadow-[0_12px_32px_rgba(14,55,64,0.45)]",
    ringColor: "border-[#d7ad7b]/20",
    gradId: "flourishTealGold",
    flourishGradStart: "#fce2b8",
    flourishGradMid: "#d7ad7b",
    flourishGradEnd: "#94672e",
    ribbonDark: ["#0b2f37", "#061a1f", "#0c353e", "#051519"],
    ribbonMid: ["#d7ad7b", "#faeade", "#be8d4e", "#8c622c"],
    ribbonFront: ["#e2bf93", "#fcf3e8", "#ce9f65", "#9e7136"],
    ribbonStroke: "#faebd7",
    medallionOuterBorder: "border-[#d7ad7b]",
    medallionBg: "radial-gradient(circle at 35% 30%, #fce2b8 0%, #d7ad7b 55%, #94672e 100%)",
    medallionInitialColor: "#0e3740",
    medallionShadow: "shadow-[inset_0_2px_4px_rgba(255,255,255,0.6),0_4px_14px_rgba(215,173,123,0.35)]",
    titleColor: "text-white",
    titleDropShadow: "drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]",
    taglineColor: "text-[#d7ad7b]",
    dividerGrad: "from-transparent via-[#d7ad7b] to-[#94672e]",
    boxBg: "bg-[#0b2f37]/88 border-[#d7ad7b]/40 shadow-[0_6px_20px_rgba(0,0,0,0.3)]",
    boxTitleDefault: "#ffffff",
    boxSubtextColor: "text-[#bce0e6]",
    ornamentType: "diamond",
    sparkleColor: "#d7ad7b",
  },
];

const DEFAULT_FORM = {
  storeName: "",
  tagline: "",
  logoUrl: "",
  welcomeTitle: "Welcome",
  welcomeSubtext: "",
  cardTemplate: "royal_gold",
  branchName: "",
  locationAddress: "",
  contactPhone: "",
  whatsappNumber: "",
  mapUrl: "",
  primaryColor: "#b78a39",
  isActive: true,
  Location: 1,
  locationId: 1,
};

// ── Jewellery Necklace / Beaded Haar SVG Garland ────────────────────────────
function JewelleryNecklaceGarland({ className = "", beadColor = "#f3ce65", chainColor = "#b88a2e" }) {
  const beadPositions = [20, 48, 80, 115, 140, 160, 180, 205, 240, 272, 300];
  return (
    <svg viewBox="0 0 320 38" className={className} fill="none">
      <path d="M 12,6 Q 160,34 308,6" stroke={chainColor} strokeWidth="1" strokeDasharray="2 3" opacity="0.75" />
      <path d="M 32,10 Q 160,38 288,10" stroke={chainColor} strokeWidth="0.8" opacity="0.5" />
      {beadPositions.map((x, i) => {
        const y = 6 + Math.sin((x / 320) * Math.PI) * 26;
        const isCenter = x === 160;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={isCenter ? 3.8 : 2.3} fill={beadColor} stroke={chainColor} strokeWidth="0.6" />
            {isCenter && (
              <g>
                <path d="M 160,33 L 163,39 L 160,43 L 157,39 Z" fill={beadColor} stroke={chainColor} strokeWidth="0.5" />
                <circle cx={160} cy={38} r={1.2} fill="#ffffff" />
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── Diamond Sparkle Vector Component ─────────────────────────────────────────
function DiamondSparkle({ className = "", color = "#ffffff", size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} className={className}>
      <path d="M12,0 L14,9 L23,12 L14,15 L12,24 L10,15 L1,12 L10,9 Z" opacity="0.95" />
      <circle cx="12" cy="12" r="1.6" fill="#ffffff" />
    </svg>
  );
}

// ── Botanical Flourish Vector Component ──────────────────────────────────────
function BotanicalFlourish({ className = "", gradId = "leafGrad", startColor = "#c5a458", midColor = "#9e7b2c", endColor = "#7a5717" }) {
  return (
    <svg
      viewBox="0 0 160 210"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={startColor} />
          <stop offset="50%" stopColor={midColor} />
          <stop offset="100%" stopColor={endColor} />
        </linearGradient>
      </defs>

      {/* Main Arching Stem */}
      <path
        d="M 10,5 C 25,35 38,80 26,130 C 18,162 6,188 2,205"
        stroke={`url(#${gradId})`}
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Top Leaf Cluster */}
      <path d="M 12,18 C 22,8 42,4 58,12 C 48,22 32,28 15,22 Z" fill={`url(#${gradId})`} opacity="0.85" />
      <path d="M 20,32 C 38,20 62,22 75,34 C 58,40 38,42 22,35 Z" fill={`url(#${gradId})`} opacity="0.9" />
      <path d="M 26,48 C 46,38 72,44 82,60 C 64,64 45,60 28,52 Z" fill={`url(#${gradId})`} opacity="0.92" />
      <path d="M 18,12 C 30,0 50,0 62,5 C 48,15 32,18 18,12 Z" fill={`url(#${gradId})`} opacity="0.75" />

      {/* Side Branch with Foliage */}
      <path
        d="M 28,58 Q 50,65 65,82 Q 72,92 70,105"
        stroke={`url(#${gradId})`}
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M 45,68 C 60,60 78,68 85,82 C 72,88 56,84 46,74 Z" fill={`url(#${gradId})`} opacity="0.85" />
      <path d="M 58,84 C 74,78 92,88 95,102 C 82,106 68,100 60,90 Z" fill={`url(#${gradId})`} opacity="0.88" />
      <path d="M 68,102 C 82,100 92,112 90,125 C 78,124 70,115 68,104 Z" fill={`url(#${gradId})`} opacity="0.85" />

      {/* Middle Cascading Leaves */}
      <path d="M 27,82 C 45,74 65,82 72,98 C 55,102 38,96 26,86 Z" fill={`url(#${gradId})`} opacity="0.9" />
      <path d="M 25,110 C 42,105 60,118 64,134 C 48,136 34,128 24,115 Z" fill={`url(#${gradId})`} opacity="0.88" />
      <path d="M 18,138 C 34,135 48,148 50,164 C 36,164 24,154 17,142 Z" fill={`url(#${gradId})`} opacity="0.85" />
      <path d="M 12,165 C 25,165 35,178 34,192 C 22,190 14,180 11,168 Z" fill={`url(#${gradId})`} opacity="0.8" />

      {/* Tendrils & Buds */}
      <path
        d="M 72,28 Q 85,32 90,44 Q 92,54 84,60 Q 76,64 74,56 Q 74,48 82,46"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="88" cy="40" r="2.5" fill={`url(#${gradId})`} />
      <circle cx="95" cy="78" r="2" fill={`url(#${gradId})`} />
      <circle cx="98" cy="98" r="2.2" fill={`url(#${gradId})`} />
      <circle cx="72" cy="138" r="2" fill={`url(#${gradId})`} />
      <circle cx="52" cy="172" r="1.8" fill={`url(#${gradId})`} />
    </svg>
  );
}

export default function AppConfigMaster({ onClose, zIndex = 101 }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const activeDbCode = dbCode || sessionStorage.getItem("dbCode") || localStorage.getItem("dbCode") || "test";

  // Active user location from Redux / session
  const activeLocation = useSelector(selectActiveLocation);
  const permissions = useSelector(selectMenuPermissions);

  const formPermissions = permissions?.["appConfigMaster"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;
  const defaultLocId = Number(
    activeLocation?.id ||
    sessionStorage.getItem("loginLocationId") ||
    localStorage.getItem("loginLocationId") ||
    1
  );

  const [selectedLocationId, setSelectedLocationId] = useState(defaultLocId);
  const [locationList, setLocationList] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("branding"); // "branding" | "branch" | "templates"
  const [previewDevice, setPreviewDevice] = useState("mobile"); // "mobile" | "tablet"
  const templateScrollRef = useRef(null);

  // Logo CDN candidates & multi-level fallback resolver
  const logoCandidates = useMemo(() => getImageCandidates(form.logoUrl), [form.logoUrl]);
  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const [logoLoadStatus, setLogoLoadStatus] = useState("idle"); // "idle" | "loading" | "loaded" | "error"
  const [logoRetryNonce, setLogoRetryNonce] = useState(0);

  // Reset candidate index & status when URL or retry nonce changes
  useEffect(() => {
    setLogoCandidateIndex(0);
    setLogoLoadStatus(form.logoUrl ? "loading" : "idle");
  }, [form.logoUrl, logoRetryNonce]);

  const activeLogoSrc = logoCandidates[logoCandidateIndex] || "";

  const handleLogoLoad = useCallback(() => {
    setLogoLoadStatus("loaded");
  }, []);

  const handleLogoError = useCallback(
    (failedSrc) => {
      setLogoCandidateIndex((currIndex) => {
        if (logoCandidates[currIndex] === failedSrc) {
          if (currIndex + 1 < logoCandidates.length) {
            setLogoLoadStatus("loading");
            return currIndex + 1;
          } else {
            setLogoLoadStatus("error");
            return currIndex;
          }
        }
        return currIndex;
      });
    },
    [logoCandidates]
  );

  const handleLogoRetry = useCallback(() => {
    setLogoCandidateIndex(0);
    setLogoLoadStatus("loading");
    setLogoRetryNonce((n) => n + 1);
  }, []);

  const scrollTemplates = (direction) => {
    if (templateScrollRef.current) {
      const scrollAmount = direction === "left" ? -230 : 230;
      templateScrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const set = (field, val) => {
    if (field === "logoUrl") {
      setLogoCandidateIndex(0);
      setLogoLoadStatus(val ? "loading" : "idle");
    }
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  // Current location name
  const currentLocObj = locationList.find((l) => Number(l.id) === Number(selectedLocationId));
  const currentLocationName = currentLocObj ? currentLocObj.name : (activeLocation?.name || "Main Branch");

  // Compute store initials, normalized logo, and validity
  const storeInitials = getStoreInitials(form.storeName);
  const normalizedLogo = normalizeImageUrl(form.logoUrl);
  const hasValidLogo = Boolean(form.logoUrl) && logoLoadStatus !== "error";
  const isGoogleDriveLink = Boolean(
    form.logoUrl &&
      (form.logoUrl.includes("drive.google.com") ||
        form.logoUrl.includes("googleusercontent.com") ||
        extractGoogleDriveId(form.logoUrl))
  );

  // Active theme configuration
  const activeTemplate =
    TEMPLATES.find((t) => t.id === form.cardTemplate) || TEMPLATES[0];

  // ── 1. Fetch available branch locations from company master ────────────────
  const fetchLocations = useCallback(async () => {
    if (!activeDbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${activeDbCode}`);
      const recs = res.data || [];
      const locs = recs
        .filter((r) => String(r.Type || r.type || "").toLowerCase() === "location")
        .map((r) => ({
          id: Number(r.auto),
          name: r.Name || r.name || `Location ${r.auto}`,
        }));
      if (locs.length > 0) {
        setLocationList(locs);
      } else {
        setLocationList([{ id: defaultLocId, name: activeLocation?.name || "SHOP (Main)" }]);
      }
    } catch (err) {
      console.error("fetchLocations error:", err.message);
      setLocationList([{ id: defaultLocId, name: activeLocation?.name || "SHOP (Main)" }]);
    }
  }, [activeDbCode, apiBaseUrl, defaultLocId, activeLocation]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // ── 2. Fetch existing config for the selected location ────────────────────
  const fetchConfig = useCallback(async (targetLocId) => {
    if (!activeDbCode) return;
    const locId = targetLocId !== undefined ? targetLocId : selectedLocationId;
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiBaseUrl}/main/appconfig/${activeDbCode}/storeBranding?locationId=${locId}`
      );
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setLogoCandidateIndex(0);
        setLogoLoadStatus(data?.logoUrl ? "loading" : "idle");
        const matchingLoc = locationList.find((l) => Number(l.id) === Number(locId));
        setForm({
          ...DEFAULT_FORM,
          ...data,
          branchName: data.branchName || matchingLoc?.name || "",
          Location: Number(locId),
          locationId: Number(locId),
        });
      }
    } catch (err) {
      console.error("AppConfigMaster fetch error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, activeDbCode, selectedLocationId, locationList]);

  useEffect(() => {
    fetchConfig(selectedLocationId);
  }, [fetchConfig, selectedLocationId]);

  // ── 3. Save config for the selected location ──────────────────────────────
  const handleSave = async () => {
    if (!hasSavePermission && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to save or update app configuration.");
      return;
    }
    if (!activeDbCode) {
      alert("Database code not found.");
      return;
    }

    const locLabel = `${currentLocationName} (Location #${selectedLocationId})`;
    if (!window.confirm(`Save configuration for ${locLabel}?`)) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        Location: Number(selectedLocationId) || 1,
        locationId: Number(selectedLocationId) || 1,
        logoUrl: normalizeImageUrl(form.logoUrl),
        templateDesign: activeTemplate,
        themeConfig: {
          id: activeTemplate.id,
          name: activeTemplate.name,
          subtitle: activeTemplate.subtitle,
          paletteLabel: activeTemplate.paletteLabel,
          bgGradient: extractGradientStops(activeTemplate.cardBg, ["#fdf8eb", "#ebd7ab"]),
          borderColor: extractHexColor(activeTemplate.cardBorder, "#dec58c"),
          ringColor: extractHexColor(activeTemplate.ringColor, "#cca655"),
          titleColor: extractHexColor(activeTemplate.titleColor, "#3d2403"),
          taglineColor: extractHexColor(activeTemplate.taglineColor, "#6c4e1b"),
          dividerColor: extractHexColor(activeTemplate.dividerGrad, "#bfa05e"),
          boxTitleDefault: activeTemplate.boxTitleDefault || "#8f5b13",
          boxSubtextColor: extractHexColor(activeTemplate.boxSubtextColor, "#475569"),
          boxBg:
            activeTemplate.id === "imperial_navy"
              ? "rgba(10, 30, 61, 0.92)"
              : activeTemplate.id === "oceanic_teal"
              ? "rgba(11, 47, 55, 0.92)"
              : "rgba(255, 255, 255, 0.94)",
          boxBorder: extractHexColor(activeTemplate.cardBorder, "#dec58c"),
          medallionGrad:
            activeTemplate.id === "imperial_navy"
              ? ["#fbe08e", "#8f5d13"]
              : activeTemplate.id === "oceanic_teal"
              ? ["#fce2b8", "#d7ad7b"]
              : extractGradientStops(activeTemplate.medallionBg, ["#d8aa49", "#8c5d12"]),
          medallionBorder: extractHexColor(activeTemplate.medallionOuterBorder, "#d5b062"),
          medallionInitialColor: activeTemplate.medallionInitialColor || "#ffffff",
          ribbonDark: activeTemplate.ribbonDark ? activeTemplate.ribbonDark[0] : "#c5983c",
          ribbonMid: activeTemplate.ribbonMid ? activeTemplate.ribbonMid[0] : "#e8c775",
          ribbonFront: activeTemplate.ribbonFront ? activeTemplate.ribbonFront[0] : "#d8ab49",
          actionBtnBg:
            activeTemplate.id === "imperial_navy"
              ? "#cca655"
              : activeTemplate.id === "oceanic_teal"
              ? "#d7ad7b"
              : form.primaryColor || "#b78a39",
          actionBtnText:
            activeTemplate.id === "imperial_navy"
              ? "#05142b"
              : activeTemplate.id === "oceanic_teal"
              ? "#0e3740"
              : "#ffffff",
          closeIconColor:
            activeTemplate.id === "imperial_navy"
              ? "#fdedc2"
              : activeTemplate.id === "oceanic_teal"
              ? "#fcefdc"
              : "#523408",
          closeBtnBg:
            activeTemplate.id === "imperial_navy"
              ? "rgba(15, 45, 89, 0.75)"
              : activeTemplate.id === "oceanic_teal"
              ? "rgba(14, 52, 61, 0.75)"
              : "rgba(255, 255, 255, 0.85)",
          ornamentType: activeTemplate.ornamentType || "none",
          garlandBeadColor: activeTemplate.garlandBeadColor || null,
          garlandChainColor: activeTemplate.garlandChainColor || null,
          sparkleColor: activeTemplate.sparkleColor || null,
        },
        _updatedBy: "Admin",
      };
      const res = await axios.post(
        `${apiBaseUrl}/main/appconfig/${activeDbCode}/storeBranding?locationId=${selectedLocationId}`,
        payload
      );
      if (res.status === 200 || res.data?.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
        alert(`Configuration saved for ${locLabel}!`);
      }
    } catch (err) {
      alert("Failed to save: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshBrandingConfig = async () => {
    if (!window.confirm("Reload configuration from database?")) return;
    await fetchConfig();
    alert("Configuration reloaded.");
  };

  // ── Showrooms Directory State & Operations ──────────────────────────────
  const [showroomsList, setShowroomsList] = useState([]);
  const [loadingShowrooms, setLoadingShowrooms] = useState(false);
  const [savingShowrooms, setSavingShowrooms] = useState(false);
  const [savedShowrooms, setSavedShowrooms] = useState(false);
  const [showroomSearch, setShowroomSearch] = useState("");
  const [isEditingShowroom, setIsEditingShowroom] = useState(false);
  const [showroomForm, setShowroomForm] = useState({
    id: null,
    locationId: 0,
    branchName: "",
    city: "",
    locationAddress: "",
    contactPhone: "",
    whatsappNumber: "",
    mapUrl: "",
    isActive: true,
    isMain: false,
  });

  const fetchShowrooms = useCallback(async () => {
    if (!activeDbCode) return;
    setLoadingShowrooms(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/main/appconfig/${activeDbCode}/showroomDirectory`);
      if (res.data && res.data.success && res.data.data) {
        const list = Array.isArray(res.data.data) ? res.data.data : (res.data.data.showrooms || []);
        setShowroomsList(list);
      }
    } catch (err) {
      console.error("fetchShowrooms error:", err.message);
    } finally {
      setLoadingShowrooms(false);
    }
  }, [activeDbCode, apiBaseUrl]);

  useEffect(() => {
    fetchShowrooms();
  }, [fetchShowrooms]);

  const handleSaveShowrooms = async () => {
    if (!hasSavePermission && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to save showroom directory.");
      return;
    }
    if (!activeDbCode) {
      alert("Database code not found.");
      return;
    }
    const activeCount = showroomsList.filter((s) => s.isActive !== false).length;
    if (!window.confirm(`Save ${showroomsList.length} showrooms (${activeCount} active in app)?`)) {
      return;
    }

    setSavingShowrooms(true);
    try {
      await axios.post(`${apiBaseUrl}/main/appconfig/${activeDbCode}/showroomDirectory`, {
        showrooms: showroomsList,
        _updatedBy: "Admin",
      });
      setSavedShowrooms(true);
      setTimeout(() => setSavedShowrooms(false), 2500);
      alert(`Showroom directory saved successfully! (${activeCount} active in app)`);
    } catch (err) {
      alert("Failed to save showrooms: " + (err.response?.data?.message || err.message));
    } finally {
      setSavingShowrooms(false);
    }
  };

  const handleRefreshShowrooms = async () => {
    if (!window.confirm("Reload showrooms from database? Unsaved edits will be lost.")) return;
    await fetchShowrooms();
    alert("Showrooms reloaded.");
  };

  const handleImportFromLocations = () => {
    if (!hasSavePermission) {
      alert("Permission Denied: You do not have permission to import new locations.");
      return;
    }
    if (!locationList || locationList.length === 0) {
      alert("No registered branch locations found in Master.");
      return;
    }
    if (!window.confirm("Import missing branches from Location Master?")) return;

    let added = 0;
    const currentList = [...showroomsList];
    locationList.forEach((loc) => {
      const exists = currentList.some(
        (s) => Number(s.locationId) === Number(loc.id) || s.branchName?.trim().toLowerCase() === loc.name?.trim().toLowerCase()
      );
      if (!exists) {
        currentList.push({
          id: Date.now() + Math.random(),
          locationId: Number(loc.id),
          branchName: loc.name,
          city: loc.name.replace(/branch|showroom|store|shop/gi, "").trim() || loc.name,
          locationAddress: "",
          contactPhone: "",
          whatsappNumber: "",
          mapUrl: "",
          isActive: true,
          isMain: currentList.length === 0 || Number(loc.id) === 1,
        });
        added++;
      }
    });
    if (added > 0) {
      setShowroomsList(currentList);
      alert(`Imported ${added} branch(es). Click 'Save Showrooms' to save changes.`);
    } else {
      alert("All branches are already in the list.");
    }
  };

  const handleOpenAddShowroom = () => {
    setShowroomForm({
      id: null,
      locationId: locationList[0]?.id || 0,
      branchName: "",
      city: "",
      locationAddress: "",
      contactPhone: "",
      whatsappNumber: "",
      mapUrl: "",
      isActive: true,
      isMain: showroomsList.length === 0,
    });
  };

  const handleSelectShowroomToEdit = (item) => {
    setShowroomForm({ ...item });
  };

  const handleClearShowroomForm = () => {
    setShowroomForm({
      id: null,
      locationId: locationList[0]?.id || 0,
      branchName: "",
      city: "",
      locationAddress: "",
      contactPhone: "",
      whatsappNumber: "",
      mapUrl: "",
      isActive: true,
      isMain: false,
    });
  };

  const handleResetOrNewShowroom = () => {
    if (showroomForm.branchName?.trim() && !window.confirm("Clear form to add a new showroom?")) {
      return;
    }
    handleClearShowroomForm();
  };

  const handleUpdateExistingShowroom = () => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit showrooms.");
      return;
    }
    if (!showroomForm.branchName?.trim()) {
      alert("Please enter branch name.");
      return;
    }
    if (!window.confirm(`Update "${showroomForm.branchName}" in the list?`)) return;

    setShowroomsList((prev) =>
      prev.map((s) => (s.id === showroomForm.id ? { ...showroomForm } : s))
    );
    alert(`"${showroomForm.branchName}" updated. Click 'Save Showrooms' to save changes.`);
  };

  const handleAddShowroomToList = () => {
    if (!hasSavePermission) {
      alert("Permission Denied: You do not have permission to add new showrooms.");
      return;
    }
    if (!showroomForm.branchName?.trim()) {
      alert("Please enter branch name.");
      return;
    }
    if (!window.confirm(`Add "${showroomForm.branchName.trim()}" to showroom list?`)) return;

    const newId = Date.now();
    const newEntry = { ...showroomForm, id: newId };
    setShowroomsList((prev) => [...prev, newEntry]);
    setShowroomForm(newEntry);
    alert(`"${newEntry.branchName}" added. Click 'Save Showrooms' to save changes.`);
  };

  const updateShowroomFormField = (field, val) => {
    setShowroomForm((prev) => {
      const updated = { ...prev, [field]: val };
      if (updated.id) {
        setShowroomsList((list) =>
          list.map((s) => (s.id === updated.id ? { ...s, [field]: val } : s))
        );
      }
      return updated;
    });
  };

  const toggleLocationActive = (id) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to change showroom status.");
      return;
    }
    setShowroomsList((prev) =>
      prev.map((s) => {
        if (s.id === id) {
          const updated = { ...s, isActive: s.isActive === false };
          if (showroomForm.id === id) {
            setShowroomForm(updated);
          }
          return updated;
        }
        return s;
      })
    );
  };

  const handleCheckAllLocations = (checked = true) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to change showroom status.");
      return;
    }
    if (showroomsList.length === 0) {
      alert("No showrooms available.");
      return;
    }
    if (!window.confirm(checked ? "Enable all showrooms in app?" : "Hide all showrooms from app?")) return;

    setShowroomsList((prev) => prev.map((s) => ({ ...s, isActive: checked })));
    if (showroomForm.id) {
      setShowroomForm((prev) => ({ ...prev, isActive: checked }));
    }
    alert(checked ? "All showrooms enabled." : "All showrooms hidden.");
  };

  const handleDeleteShowroom = (item) => {
    if (!hasDeletePermission) {
      alert("Permission Denied: You do not have permission to delete showrooms.");
      return;
    }
    if (!window.confirm(`Delete "${item.branchName}"?`)) return;

    setShowroomsList((prev) => prev.filter((s) => s.id !== item.id));
    if (showroomForm.id === item.id) {
      handleClearShowroomForm();
    }
    alert(`"${item.branchName}" removed. Click 'Save Showrooms' to save changes.`);
  };

  // Auto-select first showroom if none selected
  useEffect(() => {
    if (showroomsList.length > 0 && showroomForm.id === null) {
      setShowroomForm({ ...showroomsList[0] });
    }
  }, [showroomsList]);

  const filteredShowrooms = showroomsList.filter((s) => {
    if (!showroomSearch?.trim()) return true;
    const q = showroomSearch.toLowerCase();
    return (
      (s.branchName || "").toLowerCase().includes(q) ||
      (s.city || "").toLowerCase().includes(q) ||
      (s.locationAddress || "").toLowerCase().includes(q) ||
      (s.contactPhone || "").includes(q)
    );
  });
  const activeLocationsCount = showroomsList.filter((s) => s.isActive !== false).length;

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      {/* ── Main Modal Container (Exact layout from AppBroadcastManager) ── */}
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">
        
        {/* ── Window Header ───────────────────────────────────────────────────── */}
        <WindowHeader
          title="App Configuration Manager"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f2d59] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── Top Toolbar ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200/80 px-4 py-2 shrink-0 flex flex-nowrap items-center justify-between gap-2.5 overflow-x-auto">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-11 h-11 bg-[#0d2347] rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
              <FiShoppingBag className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h1 className="text-[17px] font-bold text-slate-800 leading-tight" style={INTER}>
                App Configuration Manager
              </h1>
              <p className="text-[11.5px] text-slate-500 font-medium" style={INTER}>
                Store branding, jewellery card templates & welcome settings
              </p>
            </div>
          </div>

          {/* Controls: Location Selector + Status Stat Card + Active Toggle Switch + Refresh + Save */}
          <div className="flex items-center gap-2.5 ml-auto shrink-0">
            {/* Showroom Location Selector Dropdown */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80">
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <FiMapPin className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider leading-none" style={INTER}>
                  Showroom Location
                </span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => {
                    const newLocId = Number(e.target.value);
                    setSelectedLocationId(newLocId);
                  }}
                  className="bg-transparent font-extrabold text-xs text-slate-800 outline-none cursor-pointer pr-1 py-0.5"
                  style={INTER}
                  title="Select showroom location to view and edit its specific branding"
                >
                  {locationList.length > 0 ? (
                    locationList.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} (Loc #{loc.id})
                      </option>
                    ))
                  ) : (
                    <option value={selectedLocationId}>
                      {activeLocation?.name || "Main Showroom"} (Loc #{selectedLocationId})
                    </option>
                  )}
                </select>
              </div>
            </div>

            {/* Status Stat Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80">
              <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <FiCheckCircle className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider" style={INTER}>
                  Card Status
                </span>
                <span className={`text-xs font-extrabold leading-tight ${form.isActive ? "text-emerald-700" : "text-slate-500"}`} style={INTER}>
                  {form.isActive ? "ACTIVE" : "INACTIVE"}
                </span>
              </div>
            </div>

            {/* Active Toggle Switch Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80">
              <span className="text-xs font-bold text-slate-700" style={INTER}>
                Welcome Card Active
              </span>
              <button
                type="button"
                onClick={() => set("isActive", !form.isActive)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                  form.isActive ? "bg-emerald-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    form.isActive ? "translate-x-4" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Refresh button */}
            <button
              type="button"
              onClick={activeTab === "showrooms" ? fetchShowrooms : fetchConfig}
              disabled={activeTab === "showrooms" ? loadingShowrooms : loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-xs cursor-pointer disabled:opacity-50"
              style={INTER}
              title={activeTab === "showrooms" ? "Refresh Showrooms Directory" : "Refresh Configuration"}
            >
              <FiRefreshCw className={`w-3.5 h-3.5 text-slate-500 ${(activeTab === "showrooms" ? loadingShowrooms : loading) ? "animate-spin text-blue-600" : ""}`} />
              Refresh
            </button>

            {/* Save button */}
            <button
              type="button"
              onClick={activeTab === "showrooms" ? handleSaveShowrooms : handleSave}
              disabled={(activeTab === "showrooms" ? savingShowrooms : saving) || (!hasSavePermission && !hasEditPermission)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white shadow-xs transition ${
                (!hasSavePermission && !hasEditPermission)
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-[#b78a39] hover:bg-[#a1782f] cursor-pointer"
              }`}
              title={(!hasSavePermission && !hasEditPermission) ? "Permission Denied: Save/Edit action disabled" : ""}
              style={INTER}
            >
              {activeTab === "showrooms" ? (
                savedShowrooms ? (
                  <>
                    <FiCheck className="w-3.5 h-3.5 text-white" />
                    Saved!
                  </>
                ) : (
                  <>
                    <FiSave className="w-3.5 h-3.5 text-white" />
                    {savingShowrooms ? "Saving…" : "Save Showrooms"}
                  </>
                )
              ) : saved ? (
                <>
                  <FiCheck className="w-3.5 h-3.5 text-white" />
                  Saved!
                </>
              ) : (
                <>
                  <FiSave className="w-3.5 h-3.5 text-white" />
                  {saving ? "Saving…" : "Save"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Tab Switcher ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200 px-4 pt-0 shrink-0 flex items-end gap-0">
          {/* Store Branding tab */}
          <button
            type="button"
            onClick={() => setActiveTab("branding")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "branding"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiShoppingBag className="w-3.5 h-3.5" />
            Store Branding
          </button>

          {/* Branch & Contact tab */}
          <button
            type="button"
            onClick={() => setActiveTab("branch")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "branch"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiMapPin className="w-3.5 h-3.5" />
            Branch & Contact
          </button>

          {/* Card Templates tab (3rd) */}
          <button
            type="button"
            onClick={() => setActiveTab("templates")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "templates"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiLayout className="w-3.5 h-3.5" />
            Card Templates
            <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[9.5px] rounded-full font-bold leading-none">
              {TEMPLATES.length}
            </span>
          </button>

          {/* All Showrooms Directory tab (4th) */}
          <button
            type="button"
            onClick={() => setActiveTab("showrooms")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "showrooms"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiCompass className="w-3.5 h-3.5" />
            All Showrooms
            <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[9.5px] rounded-full font-bold leading-none">
              {showroomsList.length}
            </span>
          </button>
        </div>

        {/* ── Main Body ───────────────────────────────────────────────────────── */}
        <div className="flex-1 flex gap-4 p-4 min-h-0 bg-slate-50 overflow-hidden">
          
          {/* ── LEFT: Configuration Form Panel (50% width) ── */}
          <div className="w-[50%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0 justify-between">
            <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">

              {/* Form header banner */}
              <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                <div className="relative flex items-center justify-center shrink-0">
                  {activeTab === "branding" && <FiShoppingBag className="w-5 h-5 text-amber-300 stroke-[1.5]" />}
                  {activeTab === "branch" && <FiMapPin className="w-5 h-5 text-amber-300 stroke-[1.5]" />}
                  {activeTab === "templates" && <FiLayout className="w-5 h-5 text-amber-300 stroke-[1.5]" />}
                  {activeTab === "showrooms" && <FiCompass className="w-5 h-5 text-amber-300 stroke-[1.5]" />}
                  <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">+</span>
                </div>
                <div className="z-10 leading-tight">
                  <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                    {activeTab === "branding"
                      ? "STORE BRANDING DETAILS"
                      : activeTab === "branch"
                      ? "BRANCH & CONTACT DETAILS"
                      : activeTab === "templates"
                      ? "CARD DESIGN TEMPLATES"
                      : "ALL SHOWROOMS & BRANCH DIRECTORY"}
                  </h3>
                  <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                    {activeTab === "branding"
                      ? "Jewellery brand identity & welcome card settings"
                      : activeTab === "branch"
                      ? "Store contact & branch location details"
                      : activeTab === "templates"
                      ? `${TEMPLATES.length} luxury jewellery ornament themes with instant live preview`
                      : "Manage chain showroom locations, addresses, phone & Google Maps links"}
                  </p>
                </div>
                {/* Golden slash design block on the far right */}
                <div className="absolute right-0 top-0 h-full w-32 pointer-events-none rounded-tr-xl overflow-hidden">
                  <svg 
                    className="absolute right-0 top-0 h-full w-full pointer-events-none" 
                    viewBox="0 0 120 44" 
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient id="goldGradBm" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#a67c1e" />
                        <stop offset="30%" stopColor="#f1c40f" />
                        <stop offset="50%" stopColor="#fbf3b9" />
                        <stop offset="70%" stopColor="#f1c40f" />
                        <stop offset="100%" stopColor="#aa7c11" />
                      </linearGradient>
                      <pattern id="honeycombBm" width="6" height="6" patternUnits="userSpaceOnUse">
                        <path d="M 3 0 L 6 1.5 L 6 4.5 L 3 6 L 0 4.5 L 0 1.5 Z" fill="none" stroke="#aa7c11" strokeWidth="0.4" opacity="0.35" />
                      </pattern>
                    </defs>
                    <polygon points="78,44 120,44 120,14" fill="url(#goldGradBm)" />
                    <polygon points="78,44 120,44 120,14" fill="url(#honeycombBm)" />
                    <line x1="78" y1="44" x2="120" y2="14" stroke="url(#goldGradBm)" strokeWidth="2.2" />
                    <line x1="66" y1="44" x2="108" y2="14" stroke="url(#goldGradBm)" strokeWidth="1.5" />
                  </svg>
                </div>
              </div>

              {/* Form Controls */}
              {loading ? (
                <div className="py-14 flex justify-center text-slate-400 text-xs font-medium">
                  Loading configuration…
                </div>
              ) : activeTab === "showrooms" ? (
                /* ── TAB 4: ACTIVE SHOWROOM FORM EDITOR ── */
                <div className="flex flex-col gap-2.5 overflow-y-auto pr-0.5 flex-1 relative">
                  {/* Status header banner */}
                  <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-[#0f2d59] flex items-center justify-center font-bold text-xs shrink-0">
                        <FiEdit2 className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <span className="text-[12px] font-extrabold text-slate-800 block leading-tight" style={INTER}>
                          {showroomForm.id ? `Editing: ${showroomForm.branchName || "Showroom"}` : "Add New Showroom Branch"}
                        </span>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {showroomForm.id ? "Changes update live in the locations list on the right" : "Enter details below and click 'Add to List'"}
                        </span>
                      </div>
                    </div>
                    {showroomForm.id ? (
                      <button
                        type="button"
                        onClick={handleClearShowroomForm}
                        className="flex items-center gap-1 px-2.5 py-1 rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-[10.5px] font-bold shadow-2xs transition cursor-pointer"
                        style={INTER}
                        title="Clear form to enter a new showroom"
                      >
                        <FiPlus className="w-3 h-3 text-blue-600" />
                        + New Showroom
                      </button>
                    ) : null}
                  </div>

                  {/* Branch / Showroom Name */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiShoppingBag className="w-3 h-3 text-slate-400" />
                      Branch / Showroom Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={showroomForm.branchName}
                      onChange={(e) => updateShowroomFormField("branchName", e.target.value)}
                      placeholder="e.g. Kalladikkode Showroom"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                    <span className="text-[9px] text-slate-400 font-medium">Showroom title displayed in the customer app</span>
                  </div>

                  {/* City / Area & Linked Master Location */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiMapPin className="w-3 h-3 text-slate-400" />
                        City / Area
                      </label>
                      <input
                        type="text"
                        value={showroomForm.city}
                        onChange={(e) => updateShowroomFormField("city", e.target.value)}
                        placeholder="e.g. Kalladikkode"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                        style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiCompass className="w-3 h-3 text-slate-400" />
                        Linked Master Location
                      </label>
                      <select
                        value={showroomForm.locationId || 0}
                        onChange={(e) => updateShowroomFormField("locationId", Number(e.target.value))}
                        className="w-full h-[28px] px-2 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none focus:border-[#0f2d59] bg-white cursor-pointer"
                        style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                      >
                        <option value={0}>Standalone (Not linked)</option>
                        {locationList.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name} (Loc #{l.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Street Address */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiGlobe className="w-3 h-3 text-slate-400" />
                      Showroom Street Address
                    </label>
                    <textarea
                      rows={2}
                      value={showroomForm.locationAddress}
                      onChange={(e) => updateShowroomFormField("locationAddress", e.target.value)}
                      placeholder="e.g. Chundangathra Tower, Chungam Junction, Kalladikode, Palakkad - 678631"
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all resize-none bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                    <span className="text-[9px] text-slate-400 font-medium">Physical address for customer store navigation</span>
                  </div>

                  {/* Contact Phone & WhatsApp */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiPhone className="w-3 h-3 text-slate-400" />
                        Contact Phone
                      </label>
                      <input
                        type="text"
                        value={showroomForm.contactPhone}
                        onChange={(e) => updateShowroomFormField("contactPhone", e.target.value)}
                        placeholder="e.g. 9746332091"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                        style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiMessageSquare className="w-3 h-3 text-slate-400" />
                        WhatsApp Number
                      </label>
                      <input
                        type="text"
                        value={showroomForm.whatsappNumber}
                        onChange={(e) => updateShowroomFormField("whatsappNumber", e.target.value)}
                        placeholder="e.g. 9746332091"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                        style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                      />
                    </div>
                  </div>

                  {/* Google Maps Link */}
                  <div className="flex flex-col gap-0.5 bg-slate-50/70 p-2 rounded-lg border border-slate-200/80">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                        <FiNavigation className="w-3 h-3 text-red-500" />
                        Google Maps Location Share Link
                      </label>
                      {showroomForm.mapUrl && (
                        <a
                          href={showroomForm.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5 font-bold cursor-pointer"
                        >
                          <FiExternalLink className="w-2.5 h-2.5" /> Test Maps Link
                        </a>
                      )}
                    </div>
                    <input
                      type="text"
                      value={showroomForm.mapUrl}
                      onChange={(e) => updateShowroomFormField("mapUrl", e.target.value)}
                      placeholder="e.g. https://maps.app.goo.gl/... or https://goo.gl/maps/..."
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-mono"
                      style={{ border: "1px solid #cbd5e1", outline: "none", ...INTER }}
                    />
                    <span className="text-[9px] text-slate-400">
                      Open Google Maps ➔ Share ➔ Copy link and paste here
                    </span>
                  </div>

                  {/* Toggles: Active Checkbox & Flagship Checkbox */}
                  <div className="p-2.5 bg-slate-50/80 border border-slate-200 rounded-lg flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showroomForm.isActive}
                        onChange={(e) => updateShowroomFormField("isActive", e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-0 cursor-pointer"
                      />
                      <span className="text-[11.5px] font-bold text-slate-700" style={INTER}>
                        Active (Check to Show in Customer App)
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showroomForm.isMain}
                        onChange={(e) => updateShowroomFormField("isMain", e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-0 cursor-pointer"
                      />
                      <span className="text-[11.5px] font-bold text-amber-900" style={INTER}>
                        ⭐ Flagship / Main Branch
                      </span>
                    </label>
                  </div>

                  {/* Action Buttons for Editing vs Adding */}
                  {showroomForm.id ? (
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button
                        type="button"
                        onClick={handleUpdateExistingShowroom}
                        disabled={!hasEditPermission}
                        className={`py-2 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition ${
                          !hasEditPermission
                            ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                            : "bg-[#0f2d59] hover:bg-[#1a3d70] text-white cursor-pointer"
                        }`}
                        style={INTER}
                        title={!hasEditPermission ? "Permission Denied: Edit action disabled" : "Apply edits to this showroom branch in the directory list"}
                      >
                        <FiCheck className="w-3.5 h-3.5" />
                        Apply Edits
                      </button>
                      <button
                        type="button"
                        onClick={handleResetOrNewShowroom}
                        className="py-2 px-2 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition cursor-pointer"
                        style={INTER}
                        title="Clear form to enter a new showroom"
                      >
                        <FiPlus className="w-3.5 h-3.5" />
                        New Showroom
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAddShowroomToList}
                      disabled={!hasSavePermission}
                      className={`mt-1 w-full py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition ${
                        !hasSavePermission
                          ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                          : "bg-[#0f2d59] hover:bg-[#1a3d70] text-white cursor-pointer"
                      }`}
                      style={INTER}
                      title={!hasSavePermission ? "Permission Denied: Save action disabled" : ""}
                    >
                      <FiPlus className="w-3.5 h-3.5" />
                      Add Showroom to List
                    </button>
                  )}
                </div>
              ) : activeTab === "branding" ? (
                /* ── TAB 1: STORE BRANDING ── */
                <div className="flex flex-col gap-2.5 overflow-y-auto pr-0.5 flex-1">

                  {/* Field 1: Store Name */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiShoppingBag className="w-3 h-3 text-slate-400" />
                      Store Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.storeName}
                      onChange={(e) => set("storeName", e.target.value)}
                      placeholder="e.g. Sri Lakshmi Gold Palace"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                    <span className="text-[9px] text-slate-400 font-medium">Display name shown at the top of the welcome card</span>
                  </div>

                  {/* Field 2: Tagline */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiType className="w-3 h-3 text-slate-400" />
                      Tagline
                    </label>
                    <input
                      type="text"
                      value={form.tagline}
                      onChange={(e) => set("tagline", e.target.value)}
                      placeholder="e.g. Trusted Since 1985"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                    <span className="text-[9px] text-slate-400 font-medium">Short motto or trust badge displayed under store name</span>
                  </div>

                  {/* Field 3: Logo URL & Default Monogram Symbol */}
                  <div className="flex flex-col gap-1 bg-slate-50/70 p-2 rounded-lg border border-slate-200/80">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                        <FiLink className="w-3 h-3 text-slate-500" />
                        Logo URL (Google Drive, Dropbox, or Direct Link)
                      </label>
                      {form.logoUrl && (
                        <button
                          type="button"
                          onClick={() => set("logoUrl", "")}
                          className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-0.5 cursor-pointer"
                          title="Clear logo and use default store symbol"
                        >
                          <FiX className="w-3 h-3" />
                          Use Default Symbol
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Mini Live Logo / Symbol Preview Badge */}
                      <div
                        className="w-[34px] h-[34px] rounded-lg border border-slate-300/80 shrink-0 flex items-center justify-center overflow-hidden shadow-xs relative"
                        style={{
                          background: hasValidLogo ? "#f8fafc" : activeTemplate.medallionBg,
                        }}
                        title={hasValidLogo ? "Brand Logo Preview" : `Default Symbol: ${storeInitials}`}
                      >
                        {hasValidLogo && activeLogoSrc ? (
                          <img
                            key={activeLogoSrc}
                            src={activeLogoSrc}
                            alt="Logo preview"
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            loading="eager"
                            onLoad={handleLogoLoad}
                            onError={() => handleLogoError(activeLogoSrc)}
                          />
                        ) : (
                          <span
                            className="font-serif font-bold text-[12px] leading-none text-center"
                            style={{ color: activeTemplate.medallionInitialColor, ...SERIF }}
                          >
                            {storeInitials}
                          </span>
                        )}
                      </div>

                      {/* Input Box */}
                      <input
                        type="text"
                        value={form.logoUrl}
                        onChange={(e) => set("logoUrl", e.target.value)}
                        placeholder="Paste image link or Google Drive share link"
                        className="flex-1 h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-mono"
                        style={{ border: "1px solid #cbd5e1", outline: "none", ...INTER }}
                      />
                    </div>

                    {/* Helpful dynamic status/guide text */}
                    <div className="flex items-center justify-between text-[9.5px] pt-0.5">
                      {!form.logoUrl ? (
                        <span className="text-slate-500 font-medium">
                          No logo added. Default hallmark symbol <strong>"{storeInitials}"</strong> will be displayed.
                        </span>
                      ) : logoLoadStatus === "error" ? (
                        <span className="text-amber-700 font-medium flex items-center gap-1">
                          <span>⚠️ Could not load image from this URL. Default symbol "{storeInitials}" is shown.</span>
                          <button
                            type="button"
                            onClick={handleLogoRetry}
                            className="underline font-bold text-amber-800 hover:text-amber-900 cursor-pointer ml-1"
                          >
                            Retry
                          </button>
                        </span>
                      ) : logoLoadStatus === "loading" ? (
                        <span className="text-blue-700 font-medium flex items-center gap-1">
                          <FiRefreshCw className="w-3 h-3 text-blue-600 animate-spin" />
                          Connecting & loading image...
                        </span>
                      ) : isGoogleDriveLink ? (
                        <span className="text-emerald-700 font-semibold flex items-center gap-1">
                          <FiCheck className="w-3 h-3 text-emerald-600" />
                          Google Drive image connected & optimized
                          {logoCandidateIndex > 0 && (
                            <span className="text-[8.5px] font-normal text-emerald-600/80">
                              (via CDN mirror #{logoCandidateIndex + 1})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-emerald-700 font-semibold flex items-center gap-1">
                          <FiCheck className="w-3 h-3 text-emerald-600" />
                          Logo loaded successfully
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Field Row 4: Welcome Card Title & Primary Brand Color */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiStar className="w-3 h-3 text-slate-400" />
                        Welcome Card Title
                      </label>
                      <input
                        type="text"
                        value={form.welcomeTitle}
                        onChange={(e) => set("welcomeTitle", e.target.value)}
                        placeholder="Welcome"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                        style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiDroplet className="w-3 h-3 text-slate-400" />
                        Primary Color
                      </label>
                      <div className="flex items-center gap-1.5 h-[28px]">
                        <input
                          type="color"
                          value={form.primaryColor || "#b78a39"}
                          onChange={(e) => set("primaryColor", e.target.value)}
                          className="w-8 h-[28px] p-0.5 border border-slate-200 rounded-md cursor-pointer bg-white"
                          style={{ border: "1px solid #e2e8f0" }}
                        />
                        <input
                          type="text"
                          value={form.primaryColor}
                          onChange={(e) => set("primaryColor", e.target.value)}
                          placeholder="#b78a39"
                          className="flex-1 h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 font-mono text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all uppercase bg-white font-semibold"
                          style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Field 5: Welcome Card Subtext */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiFileText className="w-3 h-3 text-slate-400" />
                      Welcome Card Subtext
                    </label>
                    <textarea
                      rows={2}
                      value={form.welcomeSubtext}
                      onChange={(e) => set("welcomeSubtext", e.target.value)}
                      placeholder="Your trusted jewellery partner"
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all resize-none bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                    <span className="text-[9px] text-slate-400 font-medium">Greeting subtitle displayed inside the card</span>
                  </div>

                  {/* Active theme badge / link to Card Templates tab */}
                  <div className="p-2.5 bg-amber-50/70 border border-amber-200/60 rounded-lg flex items-center justify-between mt-1">
                    <div className="flex items-center gap-2">
                      <FiLayout className="w-4 h-4 text-amber-700 shrink-0" />
                      <div>
                        <span className="text-[10.5px] font-bold text-slate-700 block leading-tight" style={INTER}>
                          Card Design: <span className="text-amber-800 font-extrabold">{activeTemplate.name}</span>
                        </span>
                        <span className="text-[9px] text-slate-500 font-medium" style={INTER}>
                          {activeTemplate.subtitle}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveTab("templates")}
                      className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold rounded-md transition shadow-2xs cursor-pointer flex items-center gap-1"
                      style={INTER}
                    >
                      Browse Themes →
                    </button>
                  </div>
                </div>
              ) : activeTab === "branch" ? (
                /* ── TAB 2: BRANCH & CONTACT ── */
                <div className="flex flex-col gap-2 overflow-y-auto pr-0.5 flex-1">
                  {/* Current Active Location Banner */}
                  <div className="p-2 bg-blue-50/70 border border-blue-200/60 rounded-lg flex items-center justify-between text-blue-900 text-[11px] font-semibold">
                    <div className="flex items-center gap-1.5">
                      <FiMapPin className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span>
                        Configuring for: <strong>{currentLocationName}</strong> (Location #{selectedLocationId})
                      </span>
                    </div>
                    <span className="text-[9.5px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                      Loc #{selectedLocationId}
                    </span>
                  </div>

                  {/* Branch Name */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiMapPin className="w-3 h-3 text-slate-400" />
                      Branch Name
                    </label>
                    <input
                      type="text"
                      value={form.branchName}
                      onChange={(e) => set("branchName", e.target.value)}
                      placeholder="e.g. Main Branch"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                  </div>

                  {/* Location Address */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiGlobe className="w-3 h-3 text-slate-400" />
                      Location Address
                    </label>
                    <textarea
                      rows={2}
                      value={form.locationAddress}
                      onChange={(e) => set("locationAddress", e.target.value)}
                      placeholder="Store full street address"
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all resize-none bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                  </div>

                  {/* Contact Phone */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiPhone className="w-3 h-3 text-slate-400" />
                      Contact Phone
                    </label>
                    <input
                      type="text"
                      value={form.contactPhone}
                      onChange={(e) => set("contactPhone", e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                  </div>

                  {/* WhatsApp Number */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiMessageSquare className="w-3 h-3 text-slate-400" />
                      WhatsApp Number
                    </label>
                    <input
                      type="text"
                      value={form.whatsappNumber}
                      onChange={(e) => set("whatsappNumber", e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                      style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                    />
                  </div>

                  {/* Google Maps Location Link */}
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiMapPin className="w-3 h-3 text-rose-500" />
                        Google Maps Location Link
                      </label>
                      {form.mapUrl ? (
                        <a
                          href={form.mapUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-0.5"
                        >
                          Test Link <FiExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={form.mapUrl || ""}
                        onChange={(e) => set("mapUrl", e.target.value)}
                        placeholder="e.g. https://maps.app.goo.gl/xxx or https://goo.gl/maps/xxx"
                        className="flex-1 h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                        style={{ border: "1px solid #e2e8f0", outline: "none", ...INTER }}
                      />
                      {form.mapUrl ? (
                        <button
                          type="button"
                          onClick={() => window.open(form.mapUrl, "_blank")}
                          className="h-[28px] px-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-md text-[11px] font-bold flex items-center gap-1 cursor-pointer transition shrink-0"
                          title="Open showroom location in Google Maps"
                        >
                          <FiMapPin className="w-3 h-3 text-rose-600" /> Maps
                        </button>
                      ) : null}
                    </div>
                    <span className="text-[9px] text-slate-400 font-medium">
                      Paste the Google Maps share link so customers can tap to view showroom location and get directions
                    </span>
                  </div>
                </div>
              ) : (
                /* ── TAB 3: CARD TEMPLATES (Spacious Grid Gallery) ── */
                <div className="flex flex-col gap-3 overflow-y-auto pr-0.5 flex-1">
                  
                  {/* Section Title Header */}
                  <div className="flex items-center justify-between pb-1 border-b border-slate-100 shrink-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1" style={INTER}>
                        <FiLayout className="w-3.5 h-3.5 text-amber-600" /> Choose Card Design
                      </span>
                      <span className="text-[9.5px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" style={INTER}>
                        {TEMPLATES.length} Luxury Jewellery Themes
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium" style={INTER}>
                      Click any theme to preview live
                    </span>
                  </div>

                  {/* 2-Column Grid of Luxury Jewellery Card Templates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {TEMPLATES.map((tpl, idx) => {
                      const active = (form.cardTemplate || "royal_gold") === tpl.id;
                      const isFullWidth = false; // All 8 cards fit a perfectly balanced 4x2 grid
                      return (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => set("cardTemplate", tpl.id)}
                          className={`group relative flex flex-col p-2.5 rounded-2xl border transition-all duration-200 cursor-pointer select-none text-left ${
                            isFullWidth ? "sm:col-span-2" : ""
                          } ${
                            active
                              ? "border-[#0f2d59] bg-white ring-3 ring-[#0f2d59]/20 shadow-md transform -translate-y-0.5"
                              : "border-slate-200/90 bg-white hover:border-[#b78a39]/70 hover:shadow-md hover:-translate-y-0.5"
                          }`}
                        >
                          {/* Miniature Luxury Card Mockup Canvas */}
                          <div
                            className={`w-full rounded-xl relative overflow-hidden border border-black/10 shadow-2xs transition-transform ${
                              isFullWidth ? "h-[80px]" : "h-[88px]"
                            }`}
                            style={{ background: tpl.cardBg }}
                          >
                            {/* 1. Concentric watermark silk rings */}
                            <div className={`absolute -top-7 -right-7 w-20 h-20 rounded-full border ${tpl.ringColor} pointer-events-none opacity-60`} />
                            <div className={`absolute -top-3 -right-3 w-12 h-12 rounded-full border ${tpl.ringColor} pointer-events-none opacity-60`} />

                            {/* 2. Top-Left Botanical Filigree */}
                            <div className="absolute -top-1 -left-1 w-12 sm:w-14 h-16 sm:h-20 pointer-events-none opacity-45 overflow-hidden">
                              <BotanicalFlourish
                                className="w-full h-full"
                                gradId={`${tpl.gradId}_thumb_${idx}`}
                                startColor={tpl.flourishGradStart}
                                midColor={tpl.flourishGradMid}
                                endColor={tpl.flourishGradEnd}
                              />
                            </div>

                            {/* 3. Draped Garland or Diamond Sparkles */}
                            {tpl.ornamentType === "garland" && (
                              <div className="absolute top-1.5 left-2 right-2 pointer-events-none opacity-90">
                                <JewelleryNecklaceGarland
                                  className="w-full h-4"
                                  beadColor={tpl.garlandBeadColor || "#dfb454"}
                                  chainColor={tpl.garlandChainColor || "#b38830"}
                                />
                              </div>
                            )}

                            {tpl.ornamentType === "diamond" && (
                              <>
                                <div className="absolute top-1.5 left-5 pointer-events-none opacity-90 animate-pulse">
                                  <DiamondSparkle color={tpl.sparkleColor || "#ffffff"} size={10} />
                                </div>
                                <div className="absolute top-3 right-6 pointer-events-none opacity-90 animate-pulse" style={{ animationDelay: "500ms" }}>
                                  <DiamondSparkle color={tpl.sparkleColor || "#ffffff"} size={12} />
                                </div>
                              </>
                            )}

                            {/* 4. Center Embossed Metallic Medallion */}
                            <div className="relative z-10 flex flex-col items-center justify-center h-full pt-1">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center font-serif font-bold shadow-xs leading-none"
                                style={{
                                  background: tpl.swatchMedallion,
                                  color: tpl.swatchInitialColor,
                                  fontSize: storeInitials.length > 2 ? "7.5px" : storeInitials.length === 2 ? "8.5px" : "10px",
                                }}
                              >
                                {storeInitials}
                              </div>
                              <span
                                className="text-[8.5px] font-serif font-bold tracking-wide text-center truncate px-3 mt-1 leading-none"
                                style={{
                                  color:
                                    tpl.id === "royal_gold" || tpl.id === "temple_jewellery" || tpl.id === "rose_gold"
                                      ? "#3d2403"
                                      : tpl.id === "diamond_solitaire"
                                      ? "#0f172a"
                                      : tpl.id === "oceanic_teal"
                                      ? "#ffffff"
                                      : "#ffeed1",
                                }}
                              >
                                {tpl.name}
                              </span>
                            </div>

                            {/* 5. Metallic Ribbon Waves across bottom */}
                            <div className="absolute bottom-0 left-0 right-0 h-3 pointer-events-none opacity-85 overflow-hidden">
                              <svg viewBox="0 0 200 30" preserveAspectRatio="none" className="w-full h-full">
                                <path d="M 0,30 Q 50,8 100,20 T 200,10 L 200,30 Z" fill={tpl.ribbonFront[0]} />
                                <path d="M 0,30 Q 60,14 120,24 T 200,16 L 200,30 Z" fill={tpl.ribbonMid[0]} opacity="0.6" />
                              </svg>
                            </div>

                            {/* Active Floating Badge */}
                            {active && (
                              <div className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 bg-[#0f2d59] text-white px-2 py-0.5 rounded-full text-[8.5px] font-extrabold shadow-sm border border-white/40 tracking-wider">
                                <FiCheck className="w-2.5 h-2.5 stroke-[3]" />
                                <span>ACTIVE</span>
                              </div>
                            )}
                          </div>

                          {/* Card Information Footer */}
                          <div className="flex flex-col w-full px-0.5 pt-1.5">
                            <div className="flex items-center justify-between gap-1.5">
                              <span
                                className={`text-[12px] font-extrabold leading-tight truncate ${
                                  active ? "text-[#0f2d59]" : "text-slate-800"
                                }`}
                                style={INTER}
                                title={tpl.name}
                              >
                                {tpl.name}
                              </span>
                              <span
                                className={`text-[8.5px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 leading-none ${
                                  active ? "bg-[#0f2d59]/10 text-[#0f2d59]" : "bg-slate-100 text-slate-600"
                                }`}
                                style={INTER}
                              >
                                {tpl.id === "royal_gold"
                                  ? "Original"
                                  : tpl.id === "temple_jewellery"
                                  ? "22k Haar"
                                  : tpl.id === "diamond_solitaire"
                                  ? "Solitaire"
                                  : tpl.id === "maharani_ruby"
                                  ? "Kundan"
                                  : tpl.id === "emerald_royale"
                                  ? "Polki"
                                  : tpl.id === "rose_gold"
                                  ? "Pearl"
                                  : tpl.id === "imperial_navy"
                                  ? "24k Bullion"
                                  : "Teal Gold"}
                              </span>
                            </div>
                            <span className="text-[10px] text-slate-500 font-medium truncate mt-0.5" style={INTER}>
                              {tpl.subtitle}
                            </span>
                            <span className="text-[9px] text-amber-700 font-semibold truncate mt-0.5" style={INTER}>
                              {tpl.paletteLabel}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Form Buttons */}
              <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 shrink-0">
                <button
                  type="button"
                  onClick={activeTab === "showrooms" ? handleRefreshShowrooms : handleRefreshBrandingConfig}
                  className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs"
                  style={INTER}
                >
                  {activeTab === "showrooms" ? "Refresh Showrooms" : "Reload Config"}
                </button>
                <button
                  type="button"
                  onClick={activeTab === "showrooms" ? handleSaveShowrooms : handleSave}
                  disabled={(activeTab === "showrooms" ? savingShowrooms : saving) || (!hasSavePermission && !hasEditPermission)}
                  className={`h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs ${
                    (!hasSavePermission && !hasEditPermission)
                      ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                      : "bg-[#b78a39] hover:bg-[#a67c32] text-white cursor-pointer"
                  }`}
                  title={(!hasSavePermission && !hasEditPermission) ? "Permission Denied: Save/Edit action disabled" : ""}
                  style={INTER}
                >
                  {(activeTab === "showrooms" ? savedShowrooms : saved) ? (
                    <>
                      <FiCheck className="w-3.5 h-3.5 text-white" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <FiSave className="w-3.5 h-3.5 text-white" />
                      {(activeTab === "showrooms" ? savingShowrooms : saving)
                        ? "Saving…"
                        : activeTab === "showrooms"
                        ? "Save Showrooms"
                        : "Save Now"}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info footer notice */}
            <div className="m-2 p-2 bg-amber-50/70 border border-amber-200/60 rounded-md flex items-center gap-2 text-amber-800 shrink-0">
              <FiInfo className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <p className="text-[10px] font-medium leading-tight" style={INTER}>
                {activeTab === "showrooms"
                  ? "Changes to your showroom directory will appear immediately inside the customer app Store Locator."
                  : "Update store branding details to customize the look and feel of your SherGold mobile app."}
              </p>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Live Card Preview / Live Store Locator Preview ── */}
          <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden justify-between">
            {activeTab === "showrooms" ? (
              <>
                {/* Header for Locations Directory & Checklist */}
                <div className="p-3.5 border-b border-slate-200 bg-white shrink-0 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shadow-xs">
                      <FiList className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-slate-800 leading-none" style={INTER}>
                          Locations Directory & Checklist
                        </h3>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" style={INTER}>
                          {activeLocationsCount} of {showroomsList.length} Active in App
                        </span>
                      </div>
                      <span className="text-[11px] text-slate-400 font-medium mt-1 block" style={INTER}>
                        Check or uncheck locations to control their visibility in customer app Store Locator
                      </span>
                    </div>
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handleCheckAllLocations(true)}
                      disabled={!hasEditPermission}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${
                        !hasEditPermission
                          ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                          : "text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 cursor-pointer"
                      }`}
                      title={!hasEditPermission ? "Permission Denied: Edit action disabled" : "Check all locations to show in app"}
                      style={INTER}
                    >
                      <FiCheckSquare className={`w-3.5 h-3.5 ${!hasEditPermission ? "text-slate-400" : "text-emerald-600"}`} />
                      Check All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCheckAllLocations(false)}
                      disabled={!hasEditPermission}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${
                        !hasEditPermission
                          ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                          : "text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 cursor-pointer"
                      }`}
                      title={!hasEditPermission ? "Permission Denied: Edit action disabled" : "Uncheck all locations to hide from app"}
                      style={INTER}
                    >
                      <FiSquare className="w-3.5 h-3.5 text-slate-400" />
                      Uncheck All
                    </button>
                    <button
                      type="button"
                      onClick={handleImportFromLocations}
                      disabled={!hasSavePermission}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${
                        !hasSavePermission
                          ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                          : "text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 cursor-pointer"
                      }`}
                      title={!hasSavePermission ? "Permission Denied: Save action disabled" : "Import unadded branches from Company Location Master"}
                      style={INTER}
                    >
                      <FiRefreshCw className={`w-3 h-3 ${!hasSavePermission ? "text-slate-400" : "text-blue-600"}`} />
                      Import Locations
                    </button>
                  </div>
                </div>

                {/* Filter / Search Bar */}
                <div className="px-3.5 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 shrink-0">
                  <div className="relative flex-1 max-w-sm">
                    <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={showroomSearch}
                      onChange={(e) => setShowroomSearch(e.target.value)}
                      disabled={!hasFindPermission}
                      placeholder={!hasFindPermission ? "Search disabled (permission required)" : "Search locations by name, city, address, or phone..."}
                      className={`w-full pl-8 pr-7 py-1 text-xs border rounded-md text-slate-800 placeholder-slate-400 focus:outline-none ${
                        !hasFindPermission
                          ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                          : "bg-white border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      }`}
                      style={INTER}
                    />
                    {showroomSearch && hasFindPermission && (
                      <button
                        type="button"
                        onClick={() => setShowroomSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 font-medium" style={INTER}>
                    Showing {filteredShowrooms.length} of {showroomsList.length} locations
                  </span>
                </div>

                {/* Listing Area */}
                <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2 min-h-0 bg-slate-50/50">
                  {showroomsList.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8 text-slate-400">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                        <FiMapPin className="w-6 h-6 text-slate-300" />
                      </div>
                      <p className="text-sm font-semibold text-slate-600 mb-1" style={INTER}>
                        No showroom locations added yet
                      </p>
                      <p className="text-xs text-slate-400 max-w-sm mb-4" style={INTER}>
                        You can import all registered branches from your Company Location Master with a single click, or add them manually using the form on the left.
                      </p>
                      <button
                        type="button"
                        onClick={handleImportFromLocations}
                        disabled={!hasSavePermission}
                        className={`px-3.5 py-1.5 text-white text-xs font-bold rounded-md shadow-xs transition-colors flex items-center gap-1.5 ${
                          !hasSavePermission
                            ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                            : "bg-[#b78a39] hover:bg-[#a67c32] cursor-pointer"
                        }`}
                        title={!hasSavePermission ? "Permission Denied: Save action disabled" : ""}
                        style={INTER}
                      >
                        <FiRefreshCw className="w-3.5 h-3.5 text-white" />
                        Import from Location Master
                      </button>
                    </div>
                  ) : filteredShowrooms.length === 0 ? (
                    <div className="h-48 flex flex-col items-center justify-center text-center p-6 text-slate-400">
                      <FiSearch className="w-8 h-8 text-slate-300 mb-2" />
                      <p className="text-xs font-semibold text-slate-600" style={INTER}>
                        No locations matching "{showroomSearch}"
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowroomSearch("")}
                        className="mt-2 text-xs text-blue-600 hover:underline font-semibold cursor-pointer"
                        style={INTER}
                      >
                        Clear search filter
                      </button>
                    </div>
                  ) : (
                    filteredShowrooms.map((s, idx) => {
                      const isSelected = showroomForm.id === s.id;
                      const isActiveInApp = s.isActive !== false;
                      const hasMapsUrl = Boolean(s.mapUrl || s.googleMapsUrl);

                      return (
                        <div
                          key={s.id || idx}
                          onClick={() => handleSelectShowroomToEdit(s)}
                          className={`rounded-lg p-3 transition-all cursor-pointer border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                            isSelected
                              ? "bg-blue-50/70 border-blue-400 ring-2 ring-blue-300/50 shadow-xs"
                              : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/80 shadow-2xs"
                          }`}
                        >
                          {/* Left: Checkbox and Details */}
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            {/* Prominent Checkbox Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLocationActive(s.id);
                              }}
                              disabled={!hasEditPermission}
                              title={
                                !hasEditPermission
                                  ? "Permission Denied: Edit action disabled"
                                  : isActiveInApp
                                  ? "Checked: Visible in customer app Store Locator (Click to uncheck)"
                                  : "Unchecked: Hidden from customer app Store Locator (Click to check)"
                              }
                              className={`mt-0.5 shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-bold transition-colors ${
                                !hasEditPermission
                                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                  : isActiveInApp
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 cursor-pointer"
                                  : "bg-slate-100 text-slate-400 border-slate-300 hover:bg-slate-200 cursor-pointer"
                              }`}
                              style={INTER}
                            >
                              {isActiveInApp ? (
                                <FiCheckSquare className={`w-4 h-4 ${!hasEditPermission ? "text-slate-400" : "text-emerald-600"}`} />
                              ) : (
                                <FiSquare className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="text-[11px]">
                                {isActiveInApp ? "Active in App" : "Hidden"}
                              </span>
                            </button>

                            {/* Location Details Info */}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-bold text-slate-800 text-xs sm:text-sm truncate" style={INTER}>
                                  {s.branchName || "Unnamed Branch"}
                                </span>
                                {s.city && (
                                  <span className="bg-slate-100 text-slate-600 text-[10px] font-semibold px-2 py-0.5 rounded shrink-0 border border-slate-200" style={INTER}>
                                    📍 {s.city}
                                  </span>
                                )}
                                {s.isMain && (
                                  <span className="bg-amber-100 text-amber-800 border border-amber-300 text-[9.5px] font-bold px-1.5 py-0.5 rounded shrink-0" style={INTER}>
                                    ⭐ Main Branch
                                  </span>
                                )}
                                {s.locationId ? (
                                  <span className="bg-blue-50 text-blue-700 border border-blue-200 text-[9.5px] font-semibold px-1.5 py-0.5 rounded shrink-0" style={INTER}>
                                    Loc #{s.locationId}
                                  </span>
                                ) : null}
                              </div>

                              {/* Address */}
                              <div className="flex items-center gap-1 mt-1 text-slate-500 text-xs">
                                <FiMapPin className="w-3 h-3 text-slate-400 shrink-0" />
                                <span className="truncate text-[11.5px]" style={INTER}>
                                  {s.locationAddress || "No street address configured"}
                                </span>
                              </div>

                              {/* Contact & Map Badges */}
                              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                {s.contactPhone && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200/80" style={INTER}>
                                    <FiPhone className="w-2.5 h-2.5 text-slate-500" />
                                    {s.contactPhone}
                                  </span>
                                )}
                                {s.whatsappNumber && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200" style={INTER}>
                                    <FiMessageSquare className="w-2.5 h-2.5 text-emerald-600" />
                                    {s.whatsappNumber}
                                  </span>
                                )}
                                {hasMapsUrl ? (
                                  <a
                                    href={s.mapUrl || s.googleMapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[10.5px] font-bold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-0.5 rounded transition-colors cursor-pointer"
                                    title="Open Google Maps link in browser to verify"
                                    style={INTER}
                                  >
                                    <FiExternalLink className="w-2.5 h-2.5 text-blue-600" />
                                    Maps Link Ready ↗
                                  </a>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded" style={INTER}>
                                    ⚠️ No Maps Link
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right: Row Action Buttons */}
                          <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectShowroomToEdit(s);
                              }}
                              disabled={!hasEditPermission}
                              className={`px-2.5 py-1 text-xs font-bold rounded border transition-colors flex items-center gap-1 ${
                                !hasEditPermission
                                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                  : isSelected
                                  ? "bg-blue-600 text-white border-blue-600 shadow-xs cursor-pointer"
                                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 cursor-pointer"
                              }`}
                              style={INTER}
                              title={!hasEditPermission ? "Permission Denied: Edit action disabled" : ""}
                            >
                              <FiEdit2 className="w-3 h-3" />
                              {isSelected ? "Editing" : "Edit"}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteShowroom(s);
                              }}
                              disabled={!hasDeletePermission}
                              className={`p-1.5 rounded border transition-colors ${
                                !hasDeletePermission
                                  ? "text-slate-300 border-transparent cursor-not-allowed"
                                  : "text-slate-400 hover:text-red-600 hover:bg-red-50 border-transparent hover:border-red-200 cursor-pointer"
                              }`}
                              title={!hasDeletePermission ? "Permission Denied: Delete action disabled" : `Delete ${s.branchName}`}
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Bottom Summary / Instructions Footer */}
                <div className="p-2.5 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2 shrink-0 text-xs">
                  <div className="flex items-center gap-2 text-slate-600">
                    <FiInfo className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span style={INTER}>
                      Checked branches (<strong className="text-emerald-700 font-bold">{activeLocationsCount}</strong>) are visible in the customer mobile app. Unchecked branches are hidden.
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-medium" style={INTER}>
                      Click <strong className="text-slate-600">Save Showrooms</strong> on the left to persist changes to database.
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Header */}
                <div className="p-3.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-white shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shadow-xs">
                      <FiEye className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 leading-none" style={INTER}>
                        Live Preview — App Welcome Card
                      </h3>
                      <span className="text-[11.5px] text-slate-400 font-medium mt-1 block" style={INTER}>
                        Theme: <strong className="text-slate-700 font-bold">{activeTemplate.name}</strong> ({activeTemplate.subtitle})
                      </span>
                    </div>
                  </div>

                  {/* Device Selector */}
                  <div className="flex items-center bg-slate-100 p-0.5 rounded-lg">
                    <button
                      type="button"
                      onClick={() => setPreviewDevice("mobile")}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold transition cursor-pointer ${
                        previewDevice === "mobile"
                          ? "bg-[#0f2d59] text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      style={INTER}
                    >
                      <FiSmartphone className="w-3.5 h-3.5" />
                      Mobile
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewDevice("tablet")}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold transition cursor-pointer ${
                        previewDevice === "tablet"
                          ? "bg-[#0f2d59] text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      style={INTER}
                    >
                      <FiTablet className="w-3.5 h-3.5" />
                      Tablet
                    </button>
                  </div>
                </div>

                {/* ── CARD MOCKUP PREVIEW CANVAS ── */}
                <div className="flex-1 bg-[#f4f7fb] flex flex-col items-center p-4 sm:p-6 min-h-0 overflow-y-auto">
                  <div
                    className={`relative w-full my-auto shrink-0 ${
                      previewDevice === "tablet" ? "max-w-[460px]" : "max-w-[395px]"
                    } rounded-3xl p-5 sm:p-6 ${activeTemplate.shadow} overflow-hidden border ${activeTemplate.cardBorder} transition-all duration-300 select-none`}
                    style={{
                      background: activeTemplate.cardBg,
                    }}
                  >
                {/* 1. Concentric Silk Watermark Rings in Top-Right Corner */}
                <div className={`absolute -top-16 -right-16 w-56 h-56 rounded-full border ${activeTemplate.ringColor} pointer-events-none`} />
                <div className={`absolute -top-8 -right-8 w-40 h-40 rounded-full border ${activeTemplate.ringColor} pointer-events-none`} />
                <div className={`absolute top-0 right-0 w-24 h-24 rounded-full border ${activeTemplate.ringColor} pointer-events-none`} />

                {/* 2. Top-Left Botanical Filigree */}
                <div className="absolute -top-1 -left-1 w-36 sm:w-44 h-48 sm:h-56 pointer-events-none overflow-hidden select-none opacity-80">
                  <BotanicalFlourish
                    className="w-full h-full"
                    gradId={`${activeTemplate.gradId}_tl`}
                    startColor={activeTemplate.flourishGradStart}
                    midColor={activeTemplate.flourishGradMid}
                    endColor={activeTemplate.flourishGradEnd}
                  />
                </div>

                {/* 3. Bottom-Right Botanical Filigree */}
                <div className="absolute -bottom-1 -right-1 w-36 sm:w-44 h-48 sm:h-56 pointer-events-none overflow-hidden select-none opacity-80 transform rotate-180">
                  <BotanicalFlourish
                    className="w-full h-full"
                    gradId={`${activeTemplate.gradId}_br`}
                    startColor={activeTemplate.flourishGradStart}
                    midColor={activeTemplate.flourishGradMid}
                    endColor={activeTemplate.flourishGradEnd}
                  />
                </div>

                {/* 4. Diamond Facet Starlight Sparkles (For Solitaire & Sapphire Themes) */}
                {activeTemplate.ornamentType === "diamond" && (
                  <>
                    <div className="absolute top-7 left-14 pointer-events-none animate-pulse">
                      <DiamondSparkle color={activeTemplate.sparkleColor || "#ffffff"} size={16} />
                    </div>
                    <div className="absolute top-12 right-14 pointer-events-none animate-pulse" style={{ animationDelay: "700ms" }}>
                      <DiamondSparkle color={activeTemplate.sparkleColor || "#ffffff"} size={20} />
                    </div>
                    <div className="absolute bottom-20 left-10 pointer-events-none animate-pulse" style={{ animationDelay: "1200ms" }}>
                      <DiamondSparkle color={activeTemplate.sparkleColor || "#ffffff"} size={14} />
                    </div>
                    <div className="absolute bottom-16 right-10 pointer-events-none animate-pulse" style={{ animationDelay: "400ms" }}>
                      <DiamondSparkle color={activeTemplate.sparkleColor || "#ffffff"} size={16} />
                    </div>
                  </>
                )}

                {/* 5. Fluid 3D Metallic Ribbon Waves across Bottom */}
                <div className="absolute bottom-0 left-0 right-0 w-full h-16 sm:h-20 pointer-events-none overflow-hidden">
                  <svg viewBox="0 0 500 80" preserveAspectRatio="none" className="w-full h-full block">
                    <defs>
                      <linearGradient id={`ribbonDark_${activeTemplate.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={activeTemplate.ribbonDark[0]} />
                        <stop offset="35%" stopColor={activeTemplate.ribbonDark[1]} />
                        <stop offset="70%" stopColor={activeTemplate.ribbonDark[2]} />
                        <stop offset="100%" stopColor={activeTemplate.ribbonDark[3]} />
                      </linearGradient>
                      <linearGradient id={`ribbonMid_${activeTemplate.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={activeTemplate.ribbonMid[0]} />
                        <stop offset="25%" stopColor={activeTemplate.ribbonMid[1]} />
                        <stop offset="50%" stopColor={activeTemplate.ribbonMid[2]} />
                        <stop offset="100%" stopColor={activeTemplate.ribbonMid[3]} />
                      </linearGradient>
                      <linearGradient id={`ribbonFront_${activeTemplate.id}`} x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={activeTemplate.ribbonFront[0]} />
                        <stop offset="30%" stopColor={activeTemplate.ribbonFront[1]} />
                        <stop offset="60%" stopColor={activeTemplate.ribbonFront[2]} />
                        <stop offset="100%" stopColor={activeTemplate.ribbonFront[3]} />
                      </linearGradient>
                    </defs>
                    <path d="M 0,50 Q 90,20 185,48 T 370,42 Q 435,18 500,45 L 500,80 L 0,80 Z" fill={`url(#ribbonDark_${activeTemplate.id})`} opacity="0.85" />
                    <path d="M 0,58 Q 110,30 220,55 T 425,38 Q 470,28 500,52 L 500,80 L 0,80 Z" fill={`url(#ribbonMid_${activeTemplate.id})`} />
                    <path d="M 0,68 Q 130,42 250,62 T 500,48 L 500,80 L 0,80 Z" fill={`url(#ribbonFront_${activeTemplate.id})`} />
                    <path d="M 0,58 Q 110,30 220,55 T 425,38 Q 470,28 500,52" fill="none" stroke={activeTemplate.ribbonStroke} strokeWidth="1.2" opacity="0.75" />
                  </svg>
                </div>

                {/* 6. Card Content Stack */}
                <div className="relative z-10 flex flex-col items-center text-center">
                  
                  {/* Double-ring circular medallion */}
                  <div className={`w-[84px] h-[84px] rounded-full border-[1.5px] ${activeTemplate.medallionOuterBorder} p-[3px] flex items-center justify-center bg-transparent mb-1.5 shadow-xs relative`}>
                    <div
                      className={`w-full h-full rounded-full flex items-center justify-center relative overflow-hidden ${activeTemplate.medallionShadow}`}
                      style={{
                        background: activeTemplate.medallionBg,
                      }}
                    >
                      {hasValidLogo && activeLogoSrc ? (
                        <img
                          key={activeLogoSrc}
                          src={activeLogoSrc}
                          alt={form.storeName || "Store Logo"}
                          className="w-full h-full rounded-full object-cover"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          loading="eager"
                          onLoad={handleLogoLoad}
                          onError={() => handleLogoError(activeLogoSrc)}
                        />
                      ) : (
                        /* Default Royal Hallmark Symbol with Short of Store Name */
                        <div className="flex flex-col items-center justify-center w-full h-full select-none relative px-1">
                          {/* Delicate Hallmark Diamond Crown */}
                          <div className="opacity-80 -mb-0.5 pointer-events-none">
                            <DiamondSparkle size={13} color={activeTemplate.medallionInitialColor} />
                          </div>
                          {/* Short Store Initials (e.g. SL or SG) */}
                          <span
                            className="font-serif font-bold drop-shadow-[0_1.5px_2px_rgba(0,0,0,0.35)] select-none leading-none tracking-wider text-center"
                            style={{
                              color: activeTemplate.medallionInitialColor,
                              fontSize: storeInitials.length > 2 ? "21px" : storeInitials.length === 2 ? "26px" : "32px",
                              ...SERIF,
                            }}
                          >
                            {storeInitials}
                          </span>
                          {/* Hallmark 3-bead seal */}
                          <div className="flex items-center gap-1 mt-1 opacity-70">
                            <span className="w-1 h-1 rounded-full bg-current" style={{ color: activeTemplate.medallionInitialColor }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-current" style={{ color: activeTemplate.medallionInitialColor }} />
                            <span className="w-1 h-1 rounded-full bg-current" style={{ color: activeTemplate.medallionInitialColor }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Store Name in Playfair Display serif */}
                  {form.storeName ? (
                    <h4
                      className={`text-[20px] sm:text-[22px] font-bold ${activeTemplate.titleColor} tracking-tight leading-snug mb-0.5 ${activeTemplate.titleDropShadow}`}
                      style={SERIF}
                    >
                      {form.storeName}
                    </h4>
                  ) : null}

                  {/* Tagline with delicate horizontal divider lines */}
                  {form.tagline && (
                    <div className="flex items-center justify-center gap-2 mb-3 w-full max-w-[260px]">
                      <div className={`h-[1px] flex-1 bg-gradient-to-r ${activeTemplate.dividerGrad}`} />
                      <span
                        className={`text-[11px] font-medium tracking-wide ${activeTemplate.taglineColor} px-1 whitespace-nowrap`}
                        style={INTER}
                      >
                        {form.tagline}
                      </span>
                      <div className={`h-[1px] flex-1 bg-gradient-to-l ${activeTemplate.dividerGrad}`} />
                    </div>
                  )}

                  {/* Traditional Jewellery Beaded Haar Garland (Only for new ornament templates, NOT royal_gold) */}
                  {activeTemplate.ornamentType === "garland" && (
                    <div className="w-full max-w-[300px] -mt-1.5 mb-2 pointer-events-none">
                      <JewelleryNecklaceGarland
                        beadColor={activeTemplate.garlandBeadColor}
                        chainColor={activeTemplate.garlandChainColor}
                      />
                    </div>
                  )}

                  {/* Floating Welcome Card Box (Centered) */}
                  <div className={`w-[88%] max-w-[360px] ${activeTemplate.boxBg} backdrop-blur-xs rounded-xl px-4 py-2.5 text-center flex flex-col items-center justify-center relative z-10 transition-all`}>
                    <h5
                      className="text-[14px] font-bold mb-0.5 tracking-tight text-center"
                      style={{ color: form.primaryColor || activeTemplate.boxTitleDefault, ...INTER }}
                    >
                      {form.welcomeTitle || "Welcome"}
                    </h5>
                    <p className={`text-[11.5px] ${activeTemplate.boxSubtextColor} font-normal leading-normal text-center`} style={INTER}>
                      {form.welcomeSubtext || "Your trusted jewellery partner"}
                    </p>
                  </div>

                  {/* Showroom Branch Badge */}
                  {form.branchName && (
                    <div className="mt-2.5 px-3 py-0.5 rounded-full border border-amber-300/40 bg-amber-50/30 text-[10px] font-bold text-amber-900 tracking-wide z-10">
                      📍 {form.branchName}
                    </div>
                  )}

                  {/* Contact Action Pills */}
                  {(form.contactPhone || form.whatsappNumber) && (
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap justify-center z-10">
                      {form.contactPhone && (
                        <span className="px-2 py-0.5 rounded-md border border-slate-300/60 bg-white/80 text-[9.5px] font-semibold flex items-center gap-1 text-slate-700 shadow-2xs">
                          <FiPhone className="w-2.5 h-2.5 text-slate-600" /> {form.contactPhone}
                        </span>
                      )}
                      {form.whatsappNumber && (
                        <span className="px-2 py-0.5 rounded-md border border-emerald-300/60 bg-emerald-50/90 text-[9.5px] font-semibold flex items-center gap-1 text-emerald-800 shadow-2xs">
                          <FiMessageSquare className="w-2.5 h-2.5 text-emerald-600" /> WhatsApp
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Info Banner */}
            <div className="m-3.5 p-2.5 bg-blue-50/70 border border-blue-200/80 rounded-xl flex items-center gap-2 text-blue-700 text-[12px] shrink-0">
              <FiInfo className="w-4 h-4 text-blue-600 shrink-0" />
              <span style={INTER}>
                This is a live preview. Changes will be reflected immediately after saving.
              </span>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
